import { Form, InputGroup } from "react-bootstrap";
import IconSearch from "../assets/images/icon-search.svg"
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { fuzzyFilter } from "../utils/FuzzySearch";

interface CommonSearchProps {
    className?: string;
    onSearch?: (query: string) => void | "";
    pageIndex?: (index: number) => void;
}

const RECENT_SEARCHES_KEY = "waiter_recent_searches";

const CommonSearch: React.FC<CommonSearchProps> = ({ className, onSearch, pageIndex }) => {
    const location = useLocation();
    const [keyword, setKeyword] = useState("");
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const searchPlaceholder = useMemo(() => {
        const path = location.pathname.toLowerCase();
        if (path === "/home" || path === "/") {
            return "Search tables";
        } else if (path === "/menu") {
            return "Search category, dish";
        } else if (path === "/item-list") {
            return "Search dish";
        }
        return "Search";
    }, [location.pathname]);
    const searchRef = useRef<HTMLDivElement>(null);
    const onSearchRef = useRef(onSearch);
    const pageIndexRef = useRef(pageIndex);

    useEffect(() => {
        onSearchRef.current = onSearch;
        pageIndexRef.current = pageIndex;
    }, [onSearch, pageIndex]);

    useEffect(() => {
        setKeyword("");
        onSearchRef.current?.("");
        setShowSuggestions(false);
    }, [location.pathname]);

    useEffect(() => {
        try {
            const savedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
            if (savedSearches) {
                setRecentSearches(JSON.parse(savedSearches));
            }
        } catch {
            setRecentSearches([]);
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            onSearchRef.current?.(keyword.trim());
            pageIndexRef.current?.(0);
        }, 150);

        return () => window.clearTimeout(timeoutId);
    }, [keyword]);

    const filteredSuggestions = useMemo(() => {
        const trimmed = keyword.trim();
        if (!trimmed) {
            return recentSearches.slice(0, 5);
        }

        // Wrap strings into objects for Fuse.js, then unwrap
        const wrapped = recentSearches.map((s) => ({ value: s }));
        return fuzzyFilter(wrapped, trimmed, ["value"])
            .slice(0, 5)
            .map((item) => item.value);
    }, [keyword, recentSearches]);

    const showTypedSuggestion = keyword.trim().length > 0 && !recentSearches.some(
        (item) => item.toLowerCase() === keyword.trim().toLowerCase()
    );

    const persistSearch = (value: string) => {
        const trimmedValue = value.trim();
        if (!trimmedValue) {
            return;
        }

        const nextSearches = [
            trimmedValue,
            ...recentSearches.filter((item) => item.toLowerCase() !== trimmedValue.toLowerCase()),
        ].slice(0, 5);

        setRecentSearches(nextSearches);
        localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(nextSearches));
    };

    const submitSearch = (value: string) => {
        const trimmedValue = value.trim();
        setKeyword(trimmedValue);
        onSearchRef.current?.(trimmedValue);
        pageIndexRef.current?.(0);
        persistSearch(trimmedValue);
        setShowSuggestions(false);
    };

    const clearSearch = () => {
        setKeyword("");
        onSearchRef.current?.("");
        pageIndexRef.current?.(0);
        setShowSuggestions(false);
    };

    return (
        <div ref={searchRef} className={`common-search-wrap${className ? ' ' + className : ''}`}>
            <InputGroup className="common-search-bar me-3">
                <InputGroup.Text onClick={() => submitSearch(keyword)} style={{ cursor: "pointer" }}>
                    <img src={IconSearch} alt="Icon" width={16} />
                </InputGroup.Text>
                <Form.Control
                    placeholder={searchPlaceholder}
                    aria-label="Search"
                    className="border-0"
                    value={keyword}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter') {
                            submitSearch((e.target as HTMLInputElement).value);
                        }
                    }}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKeyword(e.target.value)}
                />
                {keyword && (
                    <button type="button" className="search-clear-btn" onClick={clearSearch} aria-label="Clear search">
                        x
                    </button>
                )}
            </InputGroup>
            {showSuggestions && (showTypedSuggestion || filteredSuggestions.length > 0) && (
                <div className="search-suggestion-popover">
                    <div className="search-suggestion-title">
                        {keyword.trim() ? "Suggestions" : "Recent searches"}
                    </div>
                    {showTypedSuggestion && (
                        <button
                            type="button"
                            className="search-suggestion-item search-suggestion-item--primary"
                            onClick={() => submitSearch(keyword)}
                        >
                            Search for "{keyword.trim()}"
                        </button>
                    )}
                    {filteredSuggestions.map((item) => (
                        <button
                            key={item}
                            type="button"
                            className="search-suggestion-item"
                            onClick={() => submitSearch(item)}
                        >
                            {item}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};
export default CommonSearch;
